import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons'
import { useToast } from '../contexts/ToastContext'
import { UserManagementProvider } from '../contexts/UserManagementContext'
import { useUserManagementContext } from '../contexts/useUserManagementContext'
import {
  useCreateUserGroupMutation,
  useDeleteUserGroupMutation,
  useUpdateUserGroupMutation,
  useUserGroupCreateInit,
  useUserGroupEditInit,
} from '../hooks/useUsers'
import { mapExistingUserConflicts } from '../api/users'
import type { ExistingUserConflict, UserGroupFormValues, UserGroupRow } from '../types/users'
import { updateSession, useAuthSession } from '../lib/session'
import { entityNameSchema, zodAntdValidator } from '../lib/validation'
import { canManageAgentBuilderDashboardAccess } from '../features/auth/permissions'

type ModalState = {
  open: boolean
  mode: 'create' | 'edit'
  groupId: string
}

type SaveStage = 'form' | 'review'

type FormValues = {
  name: string
  userIds: string[]
  reportIds: string[]
  isCustomUserGroup: boolean
  platformType: number | null
  billing: string
  profitability: string
  job: string
  teamMember: string
  wip: string
  leads: string
  quotes: string
  dataIntegrity: string
  diyReport: string
  purchaseOrders: string
  snapshot: string
  taxLodgements: string
  xero: string
  agentBuilderDashboardAccessMode: 'None' | 'Include' | 'Exclude'
  agentBuilderDashboardRootIds: string[]
}

type ReviewDiff = {
  addedUsers: string[]
  removedUsers: string[]
  addedReports: string[]
  removedReports: string[]
}

const PLATFORM_XPM = 2
const PLATFORM_BRWFM = 6
const PLATFORM_FYI = 8
const PLATFORM_NONE = 0
const SECTION_PLATFORM_OPTIONS = [
  { value: PLATFORM_NONE, label: 'None' },
  { value: PLATFORM_XPM, label: 'XPM' },
  { value: PLATFORM_BRWFM, label: 'WorkflowMax' },
  { value: PLATFORM_FYI, label: 'FYI' },
]
const AGENT_BUILDER_DASHBOARD_ACCESS_MODE_OPTIONS = [
  { value: 'None', label: 'None' },
  { value: 'Include', label: 'Include' },
  { value: 'Exclude', label: 'Exclude' },
]
const PLATFORM_TYPE_LABELS = new Map(SECTION_PLATFORM_OPTIONS.map((option) => [option.value, option.label]))
const groupNameSchema = entityNameSchema('Group Name', { minLength: 2, maxLength: 80 })

const SECTION_DEFAULT = 'None'
const SECTION_BASE_OPTIONS = [
  { label: 'None', value: 'None' },
  { label: 'Whole', value: 'Whole' },
]
const SECTION_GENERAL_OPTIONS = [
  ...SECTION_BASE_OPTIONS,
  { label: 'Client Partner', value: 'Client Partner' },
  { label: 'Client Manager', value: 'Client Manager' },
  { label: 'Job Partner', value: 'Job Partner' },
  { label: 'Job Manager', value: 'Job Manager' },
  { label: 'Team Member Time', value: 'Team Member Time' },
  { label: 'Team Manager', value: 'Team Manager' },
]
const SECTION_BRWFM_OPTIONS = [
  ...SECTION_BASE_OPTIONS,
  { label: 'Client - Client Manager', value: 'Client - Client Manager' },
  { label: 'Client - Job Manager', value: 'Client - Job Manager' },
  { label: 'Job - Client Manager', value: 'Job - Client Manager' },
  { label: 'Job - Job Manager', value: 'Job - Job Manager' },
  { label: 'Team Member Time', value: 'Team Member Time' },
  { label: 'Team Manager', value: 'Team Manager' },
]
const SECTION_JOB_OPTIONS = [
  ...SECTION_BASE_OPTIONS,
  { label: 'Client Partner', value: 'Client Partner' },
  { label: 'Client Manager', value: 'Client Manager' },
  { label: 'Job Partner', value: 'Job Partner' },
  { label: 'Job Manager', value: 'Job Manager' },
]
const SECTION_TEAM_MEMBER_OPTIONS = [
  ...SECTION_BASE_OPTIONS,
  { label: 'Team Member Time', value: 'Team Member Time' },
  { label: 'Team Manager', value: 'Team Manager' },
]
const SECTION_TAX_OPTIONS = [
  ...SECTION_BASE_OPTIONS,
  { label: 'Client Partner', value: 'Client Partner' },
  { label: 'Client Manager', value: 'Client Manager' },
]
const SECTION_SNAPSHOT_OPTIONS = [
  ...SECTION_BASE_OPTIONS,
  { label: 'Team Manager', value: 'Team Manager' },
]

type SectionFieldName = Exclude<
  keyof FormValues,
  | 'name'
  | 'userIds'
  | 'reportIds'
  | 'isCustomUserGroup'
  | 'platformType'
  | 'agentBuilderDashboardAccessMode'
  | 'agentBuilderDashboardRootIds'
>
type SectionField = {
  name: SectionFieldName
  label: string
  options: Array<{ label: string; value: string }>
}

function getSectionPermissions(values: Partial<FormValues | UserGroupFormValues>): Pick<FormValues, SectionFieldName> {
  return {
    billing: values.billing || SECTION_DEFAULT,
    profitability: values.profitability || SECTION_DEFAULT,
    job: values.job || SECTION_DEFAULT,
    teamMember: values.teamMember || SECTION_DEFAULT,
    wip: values.wip || SECTION_DEFAULT,
    leads: values.leads || SECTION_DEFAULT,
    quotes: values.quotes || SECTION_DEFAULT,
    dataIntegrity: values.dataIntegrity || SECTION_DEFAULT,
    diyReport: values.diyReport || SECTION_DEFAULT,
    purchaseOrders: values.purchaseOrders || SECTION_DEFAULT,
    snapshot: values.snapshot || SECTION_DEFAULT,
    taxLodgements: values.taxLodgements || SECTION_DEFAULT,
    xero: values.xero || SECTION_DEFAULT,
  }
}

const defaultSectionPermissions: Pick<FormValues, SectionFieldName> = {
  billing: SECTION_DEFAULT,
  profitability: SECTION_DEFAULT,
  job: SECTION_DEFAULT,
  teamMember: SECTION_DEFAULT,
  wip: SECTION_DEFAULT,
  leads: SECTION_DEFAULT,
  quotes: SECTION_DEFAULT,
  dataIntegrity: SECTION_DEFAULT,
  diyReport: SECTION_DEFAULT,
  purchaseOrders: SECTION_DEFAULT,
  snapshot: SECTION_DEFAULT,
  taxLodgements: SECTION_DEFAULT,
  xero: SECTION_DEFAULT,
}

function normalizeIds(items: string[] | undefined) {
  return Array.isArray(items) ? items : []
}

function hasMembershipChanges(initialValues: FormValues, nextValues: Partial<FormValues>) {
  const sort = (items: string[] | undefined) => [...normalizeIds(items)].sort()
  const sameUsers = JSON.stringify(sort(initialValues.userIds)) === JSON.stringify(sort(nextValues.userIds))
  const sameReports = JSON.stringify(sort(initialValues.reportIds)) === JSON.stringify(sort(nextValues.reportIds))
  return !sameUsers || !sameReports
}

function buildDiff(initialValues: FormValues, nextValues: Partial<FormValues>, labels: Map<string, string>): ReviewDiff {
  const toLabels = (ids: string[]) => ids.map((id) => labels.get(id) || id)
  const nextUserIds = normalizeIds(nextValues.userIds)
  const nextReportIds = normalizeIds(nextValues.reportIds)
  const addedUsers = toLabels(nextUserIds.filter((id) => !initialValues.userIds.includes(id)))
  const removedUsers = toLabels(initialValues.userIds.filter((id) => !nextUserIds.includes(id)))
  const addedReports = toLabels(nextReportIds.filter((id) => !initialValues.reportIds.includes(id)))
  const removedReports = toLabels(initialValues.reportIds.filter((id) => !nextReportIds.includes(id)))
  return { addedUsers, removedUsers, addedReports, removedReports }
}

function UserGroupsContent() {
  const authSession = useAuthSession()
  const { addToast } = useToast()
  const userManagement = useUserManagementContext()

  const defaultPageSize =
    authSession?.defaultPageSize && authSession.defaultPageSize > 0 ? authSession.defaultPageSize : 25
  const [pagination, setPagination] = useState({ current: 1, pageSize: defaultPageSize })

  const [modalState, setModalState] = useState<ModalState>({ open: false, mode: 'create', groupId: '' })
  const [saveStage, setSaveStage] = useState<SaveStage>('form')
  const [initialValues, setInitialValues] = useState<FormValues>({
    name: '',
    userIds: [],
    reportIds: [],
    isCustomUserGroup: false,
    platformType: PLATFORM_NONE,
    agentBuilderDashboardAccessMode: 'None',
    agentBuilderDashboardRootIds: [],
    ...defaultSectionPermissions,
  })
  const [pendingPayload, setPendingPayload] = useState<UserGroupFormValues | null>(null)
  const [reviewDiff, setReviewDiff] = useState<ReviewDiff | null>(null)
  const [conflicts, setConflicts] = useState<ExistingUserConflict[]>([])

  const [form] = Form.useForm<FormValues>()
  const watchedIsCustomUserGroup = Form.useWatch('isCustomUserGroup', form) || false
  const watchedPlatformType = Form.useWatch('platformType', form) ?? null
  const watchedAgentBuilderDashboardAccessMode = Form.useWatch('agentBuilderDashboardAccessMode', form) || 'None'
  const canManageAgentBuilderAccess = canManageAgentBuilderDashboardAccess(authSession)

  const rows = userManagement.userGroups

  const createInitQuery = useUserGroupCreateInit(
    modalState.open && modalState.mode === 'create',
    authSession?.currentClientId
  )
  const editInitQuery = useUserGroupEditInit(modalState.mode === 'edit' ? modalState.groupId : '')

  const createMutation = useCreateUserGroupMutation()
  const updateMutation = useUpdateUserGroupMutation()
  const deleteMutation = useDeleteUserGroupMutation()

  const activeInit = modalState.mode === 'create' ? createInitQuery.data : editInitQuery.data
  const userOptions = useMemo(() => activeInit?.userOptions || [], [activeInit?.userOptions])
  const reportOptions = useMemo(() => activeInit?.reportOptions || [], [activeInit?.reportOptions])
  const agentBuilderDashboardOptions = useMemo(
    () => activeInit?.agentBuilderDashboardOptions || [],
    [activeInit?.agentBuilderDashboardOptions]
  )
  const platformTypes = useMemo(
    () => {
      const initPlatformTypes = activeInit?.platformTypes ?? []
      const source = initPlatformTypes.length > 0 ? initPlatformTypes : authSession?.platformType ?? []
      return Array.from(new Set(source.map((platformType) => Number(platformType)).filter(Number.isFinite)))
    },
    [activeInit?.platformTypes, authSession?.platformType]
  )
  const platformOptions = useMemo(
    () => SECTION_PLATFORM_OPTIONS.filter((option) => option.value === PLATFORM_NONE || platformTypes.includes(option.value)),
    [platformTypes]
  )
  const selectedPlatformType = Number(watchedPlatformType) || null
  const isBr = selectedPlatformType === PLATFORM_BRWFM
  const isFyi = selectedPlatformType === PLATFORM_FYI
  const isXpm = selectedPlatformType === PLATFORM_XPM
  const showPlatformSections = selectedPlatformType !== null
  const sharedPlatformOptions = isBr ? SECTION_BRWFM_OPTIONS : SECTION_GENERAL_OPTIONS
  const sectionFields = useMemo<SectionField[]>(() => {
    const fields = new Map<SectionFieldName, SectionField>()
    const addField = (field: SectionField) => fields.set(field.name, field)
    if (showPlatformSections) {
      addField({ name: 'billing', label: 'Billing', options: sharedPlatformOptions })
      addField({ name: 'profitability', label: 'Profitability', options: sharedPlatformOptions })
    }
    if (isBr || isFyi) {
      addField({ name: 'job', label: 'Job', options: sharedPlatformOptions })
      addField({ name: 'teamMember', label: 'Team Member', options: sharedPlatformOptions })
    }
    if (isBr) {
      addField({ name: 'wip', label: 'WIP', options: sharedPlatformOptions })
      addField({ name: 'leads', label: 'Leads', options: SECTION_BRWFM_OPTIONS })
      addField({ name: 'quotes', label: 'Quotes', options: SECTION_BRWFM_OPTIONS })
      addField({ name: 'dataIntegrity', label: 'Data Integrity', options: sharedPlatformOptions })
      addField({ name: 'diyReport', label: 'DIY Report', options: SECTION_BRWFM_OPTIONS })
      addField({ name: 'purchaseOrders', label: 'Purchase Orders', options: SECTION_BRWFM_OPTIONS })
    }
    if (isFyi) {
      addField({ name: 'wip', label: 'WIP', options: sharedPlatformOptions })
      addField({ name: 'dataIntegrity', label: 'Data Integrity', options: sharedPlatformOptions })
      addField({ name: 'diyReport', label: 'DIY Report', options: sharedPlatformOptions })
    }
    if (isXpm) {
      addField({ name: 'job', label: 'Job', options: SECTION_JOB_OPTIONS })
      addField({ name: 'teamMember', label: 'Team Member', options: SECTION_TEAM_MEMBER_OPTIONS })
      addField({ name: 'wip', label: 'WIP', options: SECTION_BASE_OPTIONS })
      addField({ name: 'dataIntegrity', label: 'Data Integrity', options: SECTION_BASE_OPTIONS })
      addField({ name: 'snapshot', label: 'Snapshot', options: SECTION_SNAPSHOT_OPTIONS })
      addField({ name: 'xero', label: 'Xero', options: SECTION_BASE_OPTIONS })
      addField({ name: 'taxLodgements', label: 'Tax Lodgements', options: SECTION_TAX_OPTIONS })
    }
    return Array.from(fields.values())
  }, [isBr, isFyi, isXpm, sharedPlatformOptions, showPlatformSections])

  const optionLabelMap = useMemo(() => {
    const map = new Map<string, string>()
    userOptions.forEach((option) => map.set(option.value, option.label))
    reportOptions.forEach((option) => map.set(option.value, option.label))
    return map
  }, [reportOptions, userOptions])

  const filteredUserOptions = useMemo(() => {
    if (!watchedIsCustomUserGroup) return userOptions
    if (modalState.mode === 'create') {
      return userOptions.filter((option) => !option.isInCustomUserGroup)
    }
    return userOptions.filter(
      (option) => !option.isInCustomUserGroup || Boolean(option.groupId?.includes(modalState.groupId))
    )
  }, [modalState.groupId, modalState.mode, userOptions, watchedIsCustomUserGroup])

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }))
  }, [userManagement.userGroupSearch])

  useEffect(() => {
    if (!modalState.open || modalState.mode !== 'edit' || !editInitQuery.data?.group) return
    const group = editInitQuery.data.group
    const nextValues: FormValues = {
      name: group.name || '',
      userIds: group.userIds || [],
      reportIds: group.reportIds || [],
      isCustomUserGroup: Boolean(group.isCustomUserGroup),
      platformType: group.platformType ?? PLATFORM_NONE,
      agentBuilderDashboardAccessMode: group.agentBuilderDashboardAccessMode || 'None',
      agentBuilderDashboardRootIds: group.agentBuilderDashboardRootIds || [],
      ...getSectionPermissions(group),
    }
    form.setFieldsValue(nextValues)
    setInitialValues(nextValues)
  }, [editInitQuery.data, form, modalState.mode, modalState.open])

  useEffect(() => {
    if (!modalState.open || modalState.mode !== 'create' || !createInitQuery.data) return
    const nextValues: FormValues = {
      name: '',
      userIds: [],
      reportIds: [],
      isCustomUserGroup: false,
      platformType: PLATFORM_NONE,
      agentBuilderDashboardAccessMode: 'None',
      agentBuilderDashboardRootIds: [],
      ...defaultSectionPermissions,
    }
    form.setFieldsValue(nextValues)
    setInitialValues(nextValues)
  }, [createInitQuery.data, form, modalState.mode, modalState.open])

  useEffect(() => {
    if (!modalState.open || !watchedIsCustomUserGroup) return
    const selectedUserIds = form.getFieldValue('userIds') || []
    const allowed = new Set(filteredUserOptions.map((option) => option.value))
    const prunedUserIds = selectedUserIds.filter((userId: string) => allowed.has(userId))
    if (prunedUserIds.length !== selectedUserIds.length) {
      form.setFieldValue('userIds', prunedUserIds)
    }
  }, [filteredUserOptions, form, modalState.open, watchedIsCustomUserGroup])

  useEffect(() => {
    if (!modalState.open || watchedAgentBuilderDashboardAccessMode !== 'None') return
    if (form.getFieldValue('agentBuilderDashboardAccessMode') !== 'None') return
    form.setFieldValue('agentBuilderDashboardRootIds', [])
  }, [form, modalState.open, watchedAgentBuilderDashboardAccessMode])

  const openCreate = () => {
    form.resetFields()
    setConflicts([])
    setPendingPayload(null)
    setReviewDiff(null)
    setSaveStage('form')
    setInitialValues({
      name: '',
      userIds: [],
      reportIds: [],
      isCustomUserGroup: false,
      platformType: PLATFORM_NONE,
      agentBuilderDashboardAccessMode: 'None',
      agentBuilderDashboardRootIds: [],
      ...defaultSectionPermissions,
    })
    setModalState({ open: true, mode: 'create', groupId: '' })
  }

  const openEdit = useCallback((row: UserGroupRow) => {
    form.resetFields()
    setConflicts([])
    setPendingPayload(null)
    setReviewDiff(null)
    setSaveStage('form')
    setModalState({ open: true, mode: 'edit', groupId: row.groupId })
  }, [form])

  const closeModal = () => {
    form.resetFields()
    setConflicts([])
    setPendingPayload(null)
    setReviewDiff(null)
    setSaveStage('form')
    setModalState({ open: false, mode: 'create', groupId: '' })
  }

  const onTableChange = (nextPagination: TablePaginationConfig) => {
    const nextPageSize = nextPagination.pageSize ?? pagination.pageSize
    setPagination((prev) => ({
      ...prev,
      current: nextPagination.current ?? prev.current,
      pageSize: nextPageSize,
    }))
    if (nextPageSize !== authSession?.defaultPageSize) {
      updateSession({ defaultPageSize: nextPageSize })
    }
  }

  const submitPayload = async (payload: UserGroupFormValues) => {
    try {
      const response =
        modalState.mode === 'create'
          ? await createMutation.mutateAsync(payload)
          : await updateMutation.mutateAsync(payload)

      if (!response?.success) {
        const hitList = mapExistingUserConflicts(response)
        if (hitList.length) {
          setConflicts(hitList)
          addToast('Some selected users already belong to another custom user group.', 'warning')
          return
        }
        addToast('Unable to save user group', 'error')
        return
      }

      await userManagement.refetchGroups()
      addToast(modalState.mode === 'create' ? 'User group created' : 'User group updated', 'success')
      closeModal()
    } catch (e) {
      addToast(e instanceof Error ? e.message : 'Unable to save user group', 'error')
    }
  }

  const saveGroup = async () => {
    try {
      const values = await form.validateFields()
      setConflicts([])
      if (
        canManageAgentBuilderAccess &&
        values.agentBuilderDashboardAccessMode !== 'None' &&
        (!values.agentBuilderDashboardRootIds || values.agentBuilderDashboardRootIds.length === 0)
      ) {
        form.setFields([
          {
            name: 'agentBuilderDashboardRootIds',
            errors: ['Select at least one Agent Builder dashboard.'],
          },
        ])
        return
      }

      const payloadPlatformType = Number(values.platformType) || null
      const activeSectionFieldNames = new Set(sectionFields.map((field) => field.name))
      const rawSectionPermissions = showPlatformSections ? getSectionPermissions(values) : defaultSectionPermissions
      const sectionPermissions = Object.fromEntries(
        (Object.keys(defaultSectionPermissions) as SectionFieldName[]).map((fieldName) => [
          fieldName,
          activeSectionFieldNames.has(fieldName) ? rawSectionPermissions[fieldName] : SECTION_DEFAULT,
        ])
      ) as Pick<FormValues, SectionFieldName>

      const safeEditGroupDefaults: Partial<UserGroupFormValues> = {
        ...(modalState.mode === 'edit' ? editInitQuery.data?.group || {} : {}),
      }
      delete safeEditGroupDefaults.agentBuilderDashboardAccessMode
      delete safeEditGroupDefaults.agentBuilderDashboardRootIds

      const payload: UserGroupFormValues = {
        ...safeEditGroupDefaults,
        groupId: modalState.mode === 'edit' ? modalState.groupId : undefined,
        clientId: authSession?.currentClientId,
        name: values.name.trim(),
        userIds: values.userIds || [],
        reportIds: values.reportIds || [],
        ...sectionPermissions,
        isCustomUserGroup: modalState.mode === 'edit' ? !!initialValues.isCustomUserGroup : false,
        platformType: payloadPlatformType,
        ...(canManageAgentBuilderAccess
          ? {
              agentBuilderDashboardAccessMode: values.agentBuilderDashboardAccessMode || 'None',
              agentBuilderDashboardRootIds:
                values.agentBuilderDashboardAccessMode === 'None'
                  ? []
                  : values.agentBuilderDashboardRootIds || [],
            }
          : {}),
      }

      if (modalState.mode === 'edit' && saveStage === 'form' && hasMembershipChanges(initialValues, values)) {
        setReviewDiff(buildDiff(initialValues, values, optionLabelMap))
        setPendingPayload(payload)
        setSaveStage('review')
        return
      }

      await submitPayload(payload)
    } catch (e) {
      if (typeof e === 'object' && e !== null && 'errorFields' in e) return
      addToast(e instanceof Error ? e.message : 'Unable to save user group', 'error')
    }
  }

  const confirmReviewUpdate = async () => {
    if (!pendingPayload) return
    await submitPayload(pendingPayload)
  }

  const deleteGroup = () => {
    if (modalState.mode !== 'edit' || !modalState.groupId) return
    Modal.confirm({
      title: 'Delete user group',
      content: 'Are you sure you want to delete this user group?',
      okText: 'Delete',
      okButtonProps: { danger: true, loading: deleteMutation.isPending },
      onOk: async () => {
        try {
          await deleteMutation.mutateAsync(modalState.groupId)
          await userManagement.refetchGroups()
          addToast('User group deleted successfully', 'success')
          closeModal()
        } catch (e) {
          addToast(e instanceof Error ? e.message : 'Unable to delete user group', 'error')
          throw e
        }
      },
    })
  }

  const columns: ColumnsType<UserGroupRow> = useMemo<ColumnsType<UserGroupRow>>(
    () => {
      const tableColumns: ColumnsType<UserGroupRow> = [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name),
        render: (_, row) => (
          <button
            type="button"
            onClick={() => openEdit(row)}
            className="font-medium text-gray-900 hover:text-brand-600 text-left"
          >
            {row.name}
          </button>
        ),
      },
      {
        title: 'Members',
        dataIndex: 'memberCount',
        key: 'memberCount',
        sorter: (a, b) => a.memberCount - b.memberCount,
      },
      {
        title: 'Reports Included',
        dataIndex: 'reportCount',
        key: 'reportCount',
        sorter: (a, b) => a.reportCount - b.reportCount,
      },
      {
        title: 'Platform Type',
        dataIndex: 'platformType',
        key: 'platformType',
        render: (value: number | null | undefined) => PLATFORM_TYPE_LABELS.get(Number(value) || PLATFORM_NONE) || 'None',
        sorter: (a, b) =>
          (PLATFORM_TYPE_LABELS.get(Number(a.platformType) || PLATFORM_NONE) || '').localeCompare(
            PLATFORM_TYPE_LABELS.get(Number(b.platformType) || PLATFORM_NONE) || ''
          ),
      },
      {
        title: 'Created',
        dataIndex: 'insertDate',
        key: 'insertDate',
        render: (value: string | null | undefined) => (value ? new Date(value).toLocaleDateString() : '—'),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 80,
        render: (_, row) => (
          <Tooltip title="Edit user group">
            <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          </Tooltip>
        ),
      },
      ]
      return tableColumns.filter((column) => column.key !== 'insertDate')
    },
    [openEdit]
  )

  const initLoading =
    createInitQuery.isFetching ||
    editInitQuery.isFetching ||
    createInitQuery.isLoading ||
    editInitQuery.isLoading

  return (
    <div className="relative overflow-hidden rounded-2xl border border-brand-100 shadow-card bg-gradient-to-br from-white to-cyan-50/35">
      <div className="px-6 pt-5 pb-4 border-b border-brand-100">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold text-gray-900">User Groups</h1>
            <p className="text-sm text-gray-500 mt-1">Manage group membership and report access</p>
          </div>
          <Tag color="cyan-inverse">{rows.length} groups</Tag>
        </div>
      </div>

      <div className="p-4 bg-white/70">
        <Space wrap>
          <Button icon={<PlusOutlined />} onClick={openCreate}>
            Add
          </Button>
          <Input
            value={userManagement.userGroupSearch}
            onChange={(e) => userManagement.setUserGroupSearch(e.target.value)}
            placeholder="Search groups"
            suffix={<SearchOutlined />}
            allowClear
            style={{ width: 220 }}
          />
          <Tooltip title="Refresh table">
            <Button icon={<ReloadOutlined />} onClick={() => void userManagement.refetchGroups()} />
          </Tooltip>
          {(userManagement.groupsLoading || userManagement.groupsFetching) && (
            <span className="text-sm text-gray-500 inline-flex items-center gap-1.5">
              <span className="w-4 h-4 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
              Loading...
            </span>
          )}
        </Space>
      </div>

      <div className="p-4">
        {userManagement.groupsError && (
          <Alert
            type="error"
            showIcon
            className="mb-4"
            message={(userManagement.groupsErrorObject as Error)?.message || 'Failed to load user groups'}
          />
        )}
        <Table<UserGroupRow>
          rowKey="groupId"
          loading={userManagement.groupsLoading || userManagement.groupsFetching}
          columns={columns}
          dataSource={rows}
          onChange={onTableChange}
          locale={{ emptyText: 'No user groups found.' }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: rows.length,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
        />
      </div>

      <Modal
        open={modalState.open}
        width={640}
        styles={{
          body: {
            maxHeight: 'calc(100vh - 220px)',
            overflowY: 'auto',
            paddingRight: 8,
          },
        }}
        title={
          saveStage === 'review'
            ? 'Review Group Membership Changes'
            : modalState.mode === 'create'
              ? 'Add New User Group'
              : 'Edit User Group'
        }
        onCancel={closeModal}
        onOk={() => void (saveStage === 'review' ? confirmReviewUpdate() : saveGroup())}
        okText={saveStage === 'review' ? 'Confirm Update' : modalState.mode === 'create' ? 'Create' : 'Update'}
        okButtonProps={{
          loading:
            createMutation.isPending || updateMutation.isPending || createInitQuery.isFetching || editInitQuery.isFetching,
          disabled: initLoading,
        }}
        footer={(_, { OkBtn, CancelBtn }) => (
          <Space>
            {modalState.mode === 'edit' && saveStage === 'form' && (
              <Button danger icon={<DeleteOutlined />} loading={deleteMutation.isPending} onClick={deleteGroup}>
                Delete
              </Button>
            )}
            {saveStage === 'review' && (
              <Button onClick={() => setSaveStage('form')} disabled={createMutation.isPending || updateMutation.isPending}>
                Back
              </Button>
            )}
            <CancelBtn />
            <OkBtn />
          </Space>
        )}
      >
        {saveStage === 'review' ? (
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <Typography.Text>Confirm the following user/report membership updates.</Typography.Text>
            <Alert
              type="info"
              showIcon
              message="Only membership changes are listed below."
              className="mb-1"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Typography.Title level={5} className="!mb-2">
                  Users
                </Typography.Title>
                <Typography.Text strong className="block">
                  Added
                </Typography.Text>
                <ul className="list-disc pl-5 mb-2">
                  {(reviewDiff?.addedUsers || []).map((item) => (
                    <li key={`user-add-${item}`}>{item}</li>
                  ))}
                  {!reviewDiff?.addedUsers.length && <li>None</li>}
                </ul>
                <Typography.Text strong className="block">
                  Removed
                </Typography.Text>
                <ul className="list-disc pl-5">
                  {(reviewDiff?.removedUsers || []).map((item) => (
                    <li key={`user-remove-${item}`}>{item}</li>
                  ))}
                  {!reviewDiff?.removedUsers.length && <li>None</li>}
                </ul>
              </div>
              <div>
                <Typography.Title level={5} className="!mb-2">
                  Reports
                </Typography.Title>
                <Typography.Text strong className="block">
                  Added
                </Typography.Text>
                <ul className="list-disc pl-5 mb-2">
                  {(reviewDiff?.addedReports || []).map((item) => (
                    <li key={`report-add-${item}`}>{item}</li>
                  ))}
                  {!reviewDiff?.addedReports.length && <li>None</li>}
                </ul>
                <Typography.Text strong className="block">
                  Removed
                </Typography.Text>
                <ul className="list-disc pl-5">
                  {(reviewDiff?.removedReports || []).map((item) => (
                    <li key={`report-remove-${item}`}>{item}</li>
                  ))}
                  {!reviewDiff?.removedReports.length && <li>None</li>}
                </ul>
              </div>
            </div>
          </Space>
        ) : (
          <Form form={form} layout="vertical">
            <Form.Item
              name="name"
              label="Group Name"
              rules={[
                { validator: zodAntdValidator(groupNameSchema) },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item name="userIds" label="Users">
              <Select
                mode="multiple"
                allowClear
                placeholder="Select users"
                options={filteredUserOptions.map((option) => ({
                  label: option.label,
                  value: option.value,
                }))}
                optionFilterProp="label"
              />
            </Form.Item>

            <Form.Item name="reportIds" label="Reports">
              <Select
                mode="multiple"
                allowClear
                placeholder="Select reports"
                options={reportOptions}
                optionFilterProp="label"
              />
            </Form.Item>

            {canManageAgentBuilderAccess && (          
              <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Form.Item name="agentBuilderDashboardAccessMode" label="Agent Builder Dashboard Access">
                  <Select options={AGENT_BUILDER_DASHBOARD_ACCESS_MODE_OPTIONS} />
                </Form.Item>
              </div>
                {watchedAgentBuilderDashboardAccessMode !== 'None' && (
                  <Form.Item
                    name="agentBuilderDashboardRootIds"
                    label="Agent Builder Dashboards"
                    rules={[
                      {
                        validator: (_, value: string[] | undefined) =>
                          value && value.length > 0
                            ? Promise.resolve()
                            : Promise.reject(new Error('Select at least one Agent Builder dashboard.')),
                      },
                    ]}
                  >
                    <Select
                      mode="multiple"
                      allowClear
                      placeholder="Select dashboards"
                      options={agentBuilderDashboardOptions}
                      optionFilterProp="label"
                    />
                  </Form.Item>
                )}
              </>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Form.Item
                name="platformType"
                label="Platform Type"
                rules={[{ required: true, message: 'Platform Type is required' }]}
              >
                <Select
                  placeholder="Select platform"
                  options={platformOptions}
                  disabled={platformOptions.length === 0}
                />
              </Form.Item>
            </div>

            {sectionFields.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {sectionFields.map((field) => (
                  <Form.Item key={field.name} name={field.name} label={field.label}>
                    <Select options={field.options} />
                  </Form.Item>
                ))}
              </div>
            )}

            {conflicts.length > 0 && (
              <Alert
                type="error"
                showIcon
                message="Some users are already mapped to another custom user group."
                description={
                  <ul className="list-disc pl-5 mb-0">
                    {conflicts.map((conflict) => (
                      <li key={`${conflict.name}-${conflict.groupName}`}>
                        {conflict.name} already belongs to {conflict.groupName}
                      </li>
                    ))}
                  </ul>
                }
              />
            )}

            {initLoading && <Alert type="info" showIcon message="Loading user group options..." />}
          </Form>
        )}
      </Modal>
    </div>
  )
}

export default function UserGroups() {
  return (
    <UserManagementProvider>
      <UserGroupsContent />
    </UserManagementProvider>
  )
}
